import { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { authService } from '../../../services/authService';
import { getCountryTranslation } from '../../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../../services/departmentsTranslationsService';
import { normalizeOrgScope, normalizeOrgScopeNames, emptyOrgFilters } from './reportOrgScopeUtils';

/**
 * فلاتر الهيكل التنظيمي المشتركة لتقارير الإدارة (سلسلة البلد → … → القسم) مع قفل المستويات حسب نطاق المستخدم.
 */
export function useReportOrgScope(currentLanguage) {
  const initialScopeApplied = useRef(false);
  const skipCascadeOnceRef = useRef(false);

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [filters, setFilters] = useState(emptyOrgFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyOrgFilters);
  const [showFilters, setShowFilters] = useState(true);
  const [lockedOrgLevels, setLockedOrgLevels] = useState({
    countryId: false,
    regionId: false,
    academyId: false,
    districtId: false,
    institutionId: false,
    departmentId: false
  });

  const fetchCountries = async () => {
    try {
      const response = await api.get('/organization/countries?page=1&limit=100');
      if (response.data.success) {
        const list = (response.data.data.countries || []).map((c) => ({
          ...c,
          translatedName: getCountryTranslation(c.name, currentLanguage) || c.name || c.name_ar || c.name_fr || c.name_en
        }));
        setCountries(list);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchRegionsByCountry = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    try {
      const response = await api.get(`/organization/countries/${countryId}/regions?page=1&limit=100`);
      if (response.data.success) {
        const list = (response.data.data.regions || []).map((r) => ({
          ...r,
          translatedName: getRegionTranslation(r.name, currentLanguage)
        }));
        setRegions(list);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const fetchAcademiesByRegion = async (regionId) => {
    if (!regionId) {
      setAcademies([]);
      return;
    }
    try {
      const response = await api.get(`/organization/regions/${regionId}/academies?page=1&limit=100`);
      if (response.data.success) {
        const list = (response.data.data.academies || []).map((a) => ({
          ...a,
          translatedName: getAcademyTranslation(a.name, currentLanguage)
        }));
        setAcademies(list);
      }
    } catch (error) {
      console.error('Error fetching academies:', error);
      setAcademies([]);
    }
  };

  const fetchDistrictsByAcademy = async (academyId) => {
    if (!academyId) {
      setDistricts([]);
      return;
    }
    try {
      const response = await api.get(`/organization/academies/${academyId}/districts?page=1&limit=100`);
      if (response.data.success) {
        const list = (response.data.data.districts || []).map((d) => ({
          ...d,
          translatedName: getDistrictTranslation(d.name, currentLanguage)
        }));
        setDistricts(list);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchInstitutionsByDistrict = async (districtId) => {
    if (!districtId) {
      setInstitutions([]);
      return;
    }
    try {
      const response = await api.get(`/organization/districts/${districtId}/institutions?page=1&limit=100`);
      if (response.data.success) {
        const list = (response.data.data.institutions || []).map((i) => ({
          ...i,
          translatedName: getInstitutionTranslation(i.name, currentLanguage)
        }));
        setInstitutions(list);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
      setInstitutions([]);
    }
  };

  const fetchDepartmentsByInstitution = async (institutionId) => {
    if (!institutionId) {
      setDepartments([]);
      return;
    }
    try {
      const response = await api.get(`/organization/institutions/${institutionId}/departments?page=1&limit=100`);
      if (response.data.success) {
        const list = (response.data.data.departments || []).map((d) => ({
          ...d,
          translatedName: getDepartmentTranslation(d.name, currentLanguage)
        }));
        setDepartments(list);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const resolveOrgNamesToIdsAndApply = async (scopeNames) => {
    if (!scopeNames) return null;
    const matchName = (item, name, translated) => !name ? false : (item?.name === name || translated === name);
    const resolved = emptyOrgFilters();
    try {
      const countriesRes = await api.get('/organization/countries?page=1&limit=100').catch(() => ({}));
      const countriesList = countriesRes?.data?.data?.countries || [];
      setCountries(countriesList.map((c) => ({
        ...c,
        translatedName: getCountryTranslation(c.name, currentLanguage) || c.name || c.name_ar || c.name_fr || c.name_en
      })));
      const country = scopeNames.countryName ? countriesList.find((c) => matchName(c, scopeNames.countryName, getCountryTranslation(c?.name, currentLanguage))) : null;
      if (!country) return null;
      resolved.countryId = String(country.id);
      const regionsRes = await api.get(`/organization/countries/${country.id}/regions?page=1&limit=100`).catch(() => ({}));
      const regionsList = regionsRes?.data?.data?.regions || [];
      setRegions(regionsList.map((r) => ({ ...r, translatedName: getRegionTranslation(r.name, currentLanguage) })));
      const region = scopeNames.regionName ? regionsList.find((r) => matchName(r, scopeNames.regionName, getRegionTranslation(r?.name, currentLanguage))) : null;
      if (!region) return resolved;
      resolved.regionId = String(region.id);
      const academiesRes = await api.get(`/organization/regions/${region.id}/academies?page=1&limit=100`).catch(() => ({}));
      const academiesList = academiesRes?.data?.data?.academies || [];
      setAcademies(academiesList.map((a) => ({ ...a, translatedName: getAcademyTranslation(a.name, currentLanguage) })));
      const academy = scopeNames.academyName ? academiesList.find((a) => matchName(a, scopeNames.academyName, getAcademyTranslation(a?.name, currentLanguage))) : null;
      if (!academy) return resolved;
      resolved.academyId = String(academy.id);
      const districtsRes = await api.get(`/organization/academies/${academy.id}/districts?page=1&limit=100`).catch(() => ({}));
      const districtsList = districtsRes?.data?.data?.districts || [];
      setDistricts(districtsList.map((d) => ({ ...d, translatedName: getDistrictTranslation(d.name, currentLanguage) })));
      const district = scopeNames.districtName ? districtsList.find((d) => matchName(d, scopeNames.districtName, getDistrictTranslation(d?.name, currentLanguage))) : null;
      if (!district) return resolved;
      resolved.districtId = String(district.id);
      const institutionsRes = await api.get(`/organization/districts/${district.id}/institutions?page=1&limit=100`).catch(() => ({}));
      const institutionsList = institutionsRes?.data?.data?.institutions || [];
      setInstitutions(institutionsList.map((i) => ({ ...i, translatedName: getInstitutionTranslation(i.name, currentLanguage) })));
      const institution = scopeNames.institutionName ? institutionsList.find((i) => matchName(i, scopeNames.institutionName, getInstitutionTranslation(i?.name, currentLanguage))) : null;
      if (!institution) return resolved;
      resolved.institutionId = String(institution.id);
      const deptsRes = await api.get(`/organization/institutions/${institution.id}/departments?page=1&limit=100`).catch(() => ({}));
      const deptsList = deptsRes?.data?.data?.departments || [];
      setDepartments(deptsList.map((d) => ({ ...d, translatedName: getDepartmentTranslation(d.name, currentLanguage) })));
      const department = scopeNames.departmentName ? deptsList.find((d) => matchName(d, scopeNames.departmentName, getDepartmentTranslation(d?.name, currentLanguage))) : null;
      if (department) resolved.departmentId = String(department.id);
      return resolved;
    } catch (e) {
      console.error('resolveOrgNamesToIdsAndApply', e);
      return null;
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [currentLanguage]);

  useEffect(() => {
    if (initialScopeApplied.current) return;

    const applyScope = (scope) => {
      if (!scope || !Object.values(scope).some((v) => v !== '' && v != null)) return;
      initialScopeApplied.current = true;
      const asStrings = {
        countryId: scope.countryId ? String(scope.countryId) : '',
        regionId: scope.regionId ? String(scope.regionId) : '',
        academyId: scope.academyId ? String(scope.academyId) : '',
        districtId: scope.districtId ? String(scope.districtId) : '',
        institutionId: scope.institutionId ? String(scope.institutionId) : '',
        departmentId: scope.departmentId ? String(scope.departmentId) : ''
      };
      setFilters((prev) => ({ ...prev, ...asStrings }));
      setAppliedFilters((prev) => ({ ...prev, ...asStrings }));
      setLockedOrgLevels({
        countryId: !!asStrings.countryId,
        regionId: !!asStrings.regionId,
        academyId: !!asStrings.academyId,
        districtId: !!asStrings.districtId,
        institutionId: !!asStrings.institutionId,
        departmentId: !!asStrings.departmentId
      });
    };

    authService
      .getCurrentUser()
      .then((me) => {
        const access = me?.organizationalAccess?.[0];
        const scope = normalizeOrgScope(access);
        const scopeNames = normalizeOrgScopeNames(access);

        if (scope) {
          applyScope(scope);
          return;
        }
        if (scopeNames && (scopeNames.countryName || scopeNames.regionName || scopeNames.academyName)) {
          return resolveOrgNamesToIdsAndApply(scopeNames).then((resolved) => {
            if (resolved && (resolved.countryId || resolved.regionId || resolved.academyId)) {
              initialScopeApplied.current = true;
              skipCascadeOnceRef.current = true;
              setFilters((prev) => ({ ...prev, ...resolved }));
              setAppliedFilters((prev) => ({ ...prev, ...resolved }));
              setLockedOrgLevels({
                countryId: !!resolved.countryId,
                regionId: !!resolved.regionId,
                academyId: !!resolved.academyId,
                districtId: !!resolved.districtId,
                institutionId: !!resolved.institutionId,
                departmentId: !!resolved.departmentId
              });
              setTimeout(() => { skipCascadeOnceRef.current = false; }, 0);
            }
          });
        }
      })
      .catch(() => {});
  }, [currentLanguage]);

  useEffect(() => {
    if (skipCascadeOnceRef.current) return;
    if (filters.countryId) fetchRegionsByCountry(filters.countryId);
    else setRegions([]);
  }, [filters.countryId]);

  useEffect(() => {
    if (skipCascadeOnceRef.current) return;
    if (filters.regionId) fetchAcademiesByRegion(filters.regionId);
    else setAcademies([]);
  }, [filters.regionId]);

  useEffect(() => {
    if (skipCascadeOnceRef.current) return;
    if (filters.academyId) fetchDistrictsByAcademy(filters.academyId);
    else setDistricts([]);
  }, [filters.academyId]);

  useEffect(() => {
    if (skipCascadeOnceRef.current) return;
    if (filters.districtId) fetchInstitutionsByDistrict(filters.districtId);
    else setInstitutions([]);
  }, [filters.districtId]);

  useEffect(() => {
    if (skipCascadeOnceRef.current) return;
    if (filters.institutionId) fetchDepartmentsByInstitution(filters.institutionId);
    else setDepartments([]);
  }, [filters.institutionId]);

  const applyOrgFilters = () => {
    setAppliedFilters({ ...filters });
  };

  return {
    countries,
    regions,
    academies,
    districts,
    institutions,
    departments,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    showFilters,
    setShowFilters,
    lockedOrgLevels,
    applyOrgFilters,
    getCountryTranslation: (c) => getCountryTranslation(c?.name, currentLanguage) || c?.name || c?.name_ar || c?.name_fr || c?.name_en
  };
}
